import {
  AppModal,
  Form,
  FormSubmitButton,
  SelectFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  useCreateMatterAssignee,
  useUpdateMatterAssigneeEndTime,
} from "@/lib/data/react-query/matters/matter-assignee";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { type MatterAssignee, type MatterRole } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatUsersName } from "@/lib/utils/format";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface EditMatterAssigneeRoleSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
  assignee: Pick<MatterAssignee, "id" | "dateModified" | "user" | "role">;
}

const formSchema = z.object({
  roleId: z.string().min(1, { message: "Role is required" }),
});

export const EditMatterAssigneeRoleSlideover = ({
  open,
  setOpen,
  matterId,
  assignee,
}: EditMatterAssigneeRoleSlideoverProps) => {
  const formId = "edit-matter-assignee-role-form";
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: { roleId: assignee.role.id },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );

  const createMatterAssignee = useCreateMatterAssignee();
  const updateMatterAssigneeEndTime = useUpdateMatterAssigneeEndTime({
    invalidateOnSettled: false,
    showSuccessToast: false,
  });

  const isFormLoading =
    createMatterAssignee.isPending || updateMatterAssigneeEndTime.isPending;

  const matterRoles = matterSettingsData?.data.roles ?? [];

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const user = assignee.user;
    // If role hasn't changed, do nothing
    if (data.roleId === assignee.role.id) {
      setOpen(false);
      return;
    }
    try {
      // End-date the current assignment
      await updateMatterAssigneeEndTime.mutateAsync({
        id: assignee.id,
        matterId,
        dateTimeEnd: new Date().toISOString(),
        dateModified: assignee.dateModified ?? "",
      });
      // Create a new assignment with the selected role
      await createMatterAssignee.mutateAsync({
        dateTimeStart: new Date().toISOString(),
        matterId,
        roleId: data.roleId,
        userId: user.id,
      });
      setOpen(false);
    } catch (error) {
      // errors are handled in hooks via toasts
      console.error(error);
    }
  };

  useEffect(() => {
    if (!open) {
      form.reset({ roleId: assignee.role.id });
    }
  }, [open, assignee.role.id, form]);

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title="Update assignee role"
        description="Change the role for this assignee"
        isDirty={form.formState.isDirty}
        loading={isFormLoading}
        bodyFormId={formId}
        bodyFormOnSubmit={form.handleSubmit(onSubmit)}
        footer={
          <div className="flex w-full justify-end gap-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton loading={isFormLoading} submitFormId={formId} />
          </div>
        }
      >
        <fieldset
          className={cn(slideoverFieldsetClassName, "my-0 px-0 pt-0 pb-4")}
        >
          {isFormLoading && <FormLoadingOverlay />}
          <div className="col-span-full">
            <Typography size="sm" variant="subHeading">
              Assignee
            </Typography>
            <Typography>{formatUsersName(assignee.user)}</Typography>
          </div>
          <SelectFormField<MatterRole>
            label="Role"
            items={matterRoles}
            itemsKey="id"
            formatItemLabel={(item) => item?.label}
            register={form.register("roleId")}
            placeholder="Select a role"
          />
        </fieldset>
      </AppModal>
    </Form>
  );
};
